/* ==========================================================================
   LXT Damping — theme behaviour
   Cart drawer (AJAX add / update / remove), quantity steppers, variant
   pricing and the product gallery. Every handler is delegated from the
   document so behaviour survives the Shopify theme editor re-rendering a
   section, and every feature degrades to the native Shopify form or link
   when JavaScript is unavailable.
   ========================================================================== */

(function () {
  'use strict';

  var config = window.lxtTheme || {};
  var strings = config.strings || {};
  var icons = config.icons || {};
  var drawer = document.querySelector('[data-lxt-cart-drawer]');

  /* ---- money ----------------------------------------------------------- */

  function decodeEntities(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&#(\d+);/g, function (match, code) {
        return String.fromCharCode(parseInt(code, 10));
      })
      .replace(/&nbsp;/g, ' ');
  }

  var moneyFormat = decodeEntities(config.moneyFormat || '${{amount}}');

  function formatMoney(cents, withDecimals) {
    var precision = withDecimals ? 2 : 0;
    var amount = (cents === null || cents === undefined ? 0 : cents) / 100;
    var parts = amount.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    var value = parts.join('.');
    if (precision === 2 && parts[1] === '00') value = parts[0];

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholder);
    if (!match) return value;

    var number = value;
    if (match[1] === 'amount_with_comma_separator' || match[1] === 'amount_no_decimals_with_comma_separator') {
      number = parts.join(',');
    } else if (match[1] === 'amount_with_space_separator' || match[1] === 'amount_no_decimals_with_space_separator') {
      number = parts.join(' ');
    }
    return moneyFormat.replace(placeholder, number);
  }

  function money(cents) {
    return formatMoney(cents, false);
  }

  /* ---- request helpers ------------------------------------------------- */

  function request(url, options) {
    return fetch(url, options).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (error) {
          data = null;
        }
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || strings.addError);
        }
        return data;
      });
    });
  }

  function loadCart() {
    return request(config.cartUrl, { headers: { Accept: 'application/json' } });
  }

  function changeLine(line, quantity) {
    return request(config.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (cart) {
      renderCart(cart);
    }).catch(function (error) {
      showDrawerError(error.message);
    });
  }

  /* ---- drawer ---------------------------------------------------------- */

  function setCounts(count) {
    var counts = document.querySelectorAll('[data-lxt-cart-count]');
    var index;
    for (index = 0; index < counts.length; index++) {
      counts[index].textContent = '(' + count + ')';
    }
    var labels = document.querySelectorAll('[data-lxt-cart-count-label]');
    for (index = 0; index < labels.length; index++) {
      labels[index].textContent = '(' + count + ')';
    }
  }

  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    window.requestAnimationFrame(function () {
      drawer.classList.add('is-open');
    });
    document.body.setAttribute('data-lxt-drawer-open', '');
  }

  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.body.removeAttribute('data-lxt-drawer-open');
    window.setTimeout(function () {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 280);
  }

  function showDrawerError(message) {
    if (!drawer) return;
    var error = drawer.querySelector('[data-lxt-cart-error]');
    if (!error) return;
    error.textContent = message || strings.addError;
    error.hidden = false;
  }

  function clearDrawerError() {
    if (!drawer) return;
    var error = drawer.querySelector('[data-lxt-cart-error]');
    if (error) error.hidden = true;
  }

  function stepper(attributes, label, icon) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'lxt-qty__btn';
    button.setAttribute(attributes.name, attributes.value);
    button.setAttribute('aria-label', label);
    button.innerHTML = icon || '';
    return button;
  }

  function buildRow(item, line) {
    var row = document.createElement('div');
    row.className = 'lxt-cart__row';
    row.setAttribute('data-lxt-line', String(line));

    if (item.image) {
      var imageWrap = document.createElement('div');
      imageWrap.className = 'lxt-cart__image';
      var image = document.createElement('img');
      image.src = String(item.image).replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, '_200x$1$2');
      image.alt = item.product_title || '';
      image.width = 200;
      image.height = 200;
      image.loading = 'lazy';
      image.onerror = function () {
        image.onerror = null;
        image.src = item.image;
      };
      imageWrap.appendChild(image);
      row.appendChild(imageWrap);
    }

    var content = document.createElement('div');
    content.className = 'lxt-cart__body';

    var name = document.createElement('p');
    name.className = 'lxt-cart__name';
    var nameLink = document.createElement('a');
    nameLink.href = item.url;
    nameLink.textContent = item.product_title;
    name.appendChild(nameLink);
    content.appendChild(name);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      var variant = document.createElement('p');
      variant.className = 'lxt-cart__variant';
      variant.textContent = item.variant_title;
      content.appendChild(variant);
    }

    var control = document.createElement('div');
    control.className = 'lxt-cart__control';

    var quantity = document.createElement('div');
    quantity.className = 'lxt-qty';
    quantity.appendChild(stepper({ name: 'data-lxt-line-decrease', value: String(line) }, strings.decrease, icons.minus));
    var value = document.createElement('span');
    value.className = 'lxt-qty__value';
    value.textContent = String(item.quantity);
    quantity.appendChild(value);
    quantity.appendChild(stepper({ name: 'data-lxt-line-increase', value: String(line) }, strings.increase, icons.plus));
    control.appendChild(quantity);

    var price = document.createElement('span');
    price.className = 'lxt-cart__price';
    price.textContent = money(item.final_line_price);
    control.appendChild(price);
    content.appendChild(control);

    var removeRow = document.createElement('p');
    removeRow.className = 'lxt-cart__remove-row';
    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'lxt-cart__remove';
    remove.setAttribute('data-lxt-line-remove', String(line));
    remove.textContent = strings.remove;
    removeRow.appendChild(remove);
    content.appendChild(removeRow);

    row.appendChild(content);
    return row;
  }

  function renderCart(cart) {
    setCounts(cart.item_count);
    if (!drawer) return;

    var body = drawer.querySelector('[data-lxt-cart-body]');
    var foot = drawer.querySelector('[data-lxt-cart-foot]');
    var subtotal = drawer.querySelector('[data-lxt-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);

    body.innerHTML = '';
    clearDrawerError();

    if (!cart.item_count) {
      var state = document.createElement('div');
      state.className = 'lxt-cd__state';
      var message = document.createElement('p');
      message.textContent = strings.empty;
      state.appendChild(message);
      var link = document.createElement('a');
      link.className = 'lxt-btn';
      link.href = config.allProductsUrl;
      link.textContent = strings.shopAll;
      state.appendChild(link);
      body.appendChild(state);
    } else {
      var index;
      for (index = 0; index < cart.items.length; index++) {
        body.appendChild(buildRow(cart.items[index], index + 1));
      }
    }

    if (foot) foot.hidden = !cart.item_count;
  }

  function refreshCart(shouldOpen) {
    openDrawer();
    return loadCart().then(function (cart) {
      renderCart(cart);
    }).catch(function (error) {
      showDrawerError(error.message);
    });
  }

  /* ---- product forms --------------------------------------------------- */

  function productScope(form) {
    var scope = form.closest('[data-lxt-product]');
    if (!scope) return null;
    var script = scope.querySelector('[data-lxt-variants]');
    if (!script) return null;
    var variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
    return { scope: scope, variants: variants };
  }

  function selectedVariant(form, variants) {
    var checked = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
    var id = checked ? checked.value : null;
    var index;
    for (index = 0; index < variants.length; index++) {
      if (String(variants[index].id) === String(id)) return variants[index];
    }
    return variants[0] || null;
  }

  function refreshBuyLabel(form) {
    var button = form.querySelector('.lxt-buy__button');
    if (!button) return;
    var label = form.getAttribute('data-add-label') || strings.add;
    var data = productScope(form);
    var variant = data ? selectedVariant(form, data.variants) : null;
    if (!variant) {
      button.textContent = label;
      return;
    }
    var input = form.querySelector('[name="quantity"]');
    var quantity = input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
    button.textContent = label + ' · ' + money(variant.price * quantity);
  }

  function applyVariant(form, variant) {
    var data = productScope(form);
    if (!data || !variant) return;

    var price = data.scope.querySelector('[data-lxt-price]');
    if (price) price.textContent = money(variant.price);

    var compare = data.scope.querySelector('[data-lxt-compare]');
    if (compare) {
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (onSale) compare.textContent = money(variant.compare_at_price);
      compare.hidden = !onSale;
    }

    var stock = data.scope.querySelector('[data-lxt-stock]');
    var button = form.querySelector('.lxt-buy__button');
    var qtyInput = form.querySelector('[name="quantity"]');

    if (!variant.available) {
      if (stock) stock.textContent = strings.soldOut;
      if (qtyInput) qtyInput.disabled = true;
      if (button) {
        button.disabled = true;
        button.textContent = strings.soldOut;
      }
      return;
    }

    if (stock) stock.textContent = stock.getAttribute('data-default-label') || strings.inStock;
    if (qtyInput) qtyInput.disabled = false;
    if (button) button.disabled = false;
    refreshBuyLabel(form);
  }

  function changeQuantity(input, step) {
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 1;
    input.value = String(Math.max(1, current + step));
  }

  /* ---- events ---------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var open = target.closest('[data-lxt-cart-open]');
    if (open) {
      if (document.body.classList.contains('lxt-template--cart')) return;
      event.preventDefault();
      refreshCart(true);
      return;
    }

    if (target.closest('[data-lxt-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var thumb = target.closest('[data-lxt-gallery-thumb]');
    if (thumb) {
      var gallery = thumb.closest('[data-lxt-gallery]');
      var main = gallery ? gallery.querySelector('[data-lxt-gallery-main]') : null;
      if (gallery && main) {
        if (thumb.getAttribute('data-src')) main.src = thumb.getAttribute('data-src');
        var alt = thumb.getAttribute('data-alt');
        if (alt) main.alt = alt;
        var thumbs = gallery.querySelectorAll('[data-lxt-gallery-thumb]');
        var index;
        for (index = 0; index < thumbs.length; index++) {
          thumbs[index].classList.toggle('is-active', thumbs[index] === thumb);
        }
      }
      return;
    }

    var decrease = target.closest('[data-lxt-qty-decrease]');
    var increase = target.closest('[data-lxt-qty-increase]');
    if (decrease || increase) {
      var wrap = (decrease || increase).closest('.lxt-qty');
      var input = wrap ? wrap.querySelector('[name="quantity"]') : null;
      if (input) {
        changeQuantity(input, decrease ? -1 : 1);
        if (input.form) refreshBuyLabel(input.form);
      }
      return;
    }

    var lineDecrease = target.closest('[data-lxt-line-decrease]');
    var lineIncrease = target.closest('[data-lxt-line-increase]');
    var lineRemove = target.closest('[data-lxt-line-remove]');
    if (lineDecrease || lineIncrease || lineRemove) {
      var element = lineDecrease || lineIncrease || lineRemove;
      var row = element.closest('[data-lxt-line]');
      var line = row ? parseInt(row.getAttribute('data-lxt-line'), 10) : NaN;
      if (isNaN(line)) return;
      if (lineRemove) {
        changeLine(line, 0);
        return;
      }
      var value = row.querySelector('.lxt-qty__value');
      var quantity = value ? parseInt(value.textContent, 10) || 1 : 1;
      changeLine(line, Math.max(0, quantity + (lineDecrease ? -1 : 1)));
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || input.name !== 'id' || !input.form) return;
    if (!input.form.classList || !input.form.classList.contains('lxt-buy-form')) return;
    var data = productScope(input.form);
    if (!data) return;
    var variant = null;
    var index;
    for (index = 0; index < data.variants.length; index++) {
      if (String(data.variants[index].id) === String(input.value)) {
        variant = data.variants[index];
        break;
      }
    }
    if (variant) applyVariant(input.form, variant);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.classList || !form.classList.contains('lxt-buy-form')) return;
    if (event.defaultPrevented) return;
    event.preventDefault();

    var button = form.querySelector('.lxt-buy__button');
    if (button && button.disabled) return;
    if (button) button.setAttribute('aria-busy', 'true');
    form.setAttribute('aria-busy', 'true');

    request(config.cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    }).then(function () {
      return loadCart().then(function (fresh) {
        renderCart(fresh);
      });
    }).catch(function (error) {
      openDrawer();
      showDrawerError(error.message);
    }).then(function () {
      if (button) button.removeAttribute('aria-busy');
      form.removeAttribute('aria-busy');
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeDrawer();
  });

  /* ---- start ----------------------------------------------------------- */

  var forms = document.querySelectorAll('.lxt-buy-form');
  var index;
  for (index = 0; index < forms.length; index++) {
    var data = productScope(forms[index]);
    if (data) {
      applyVariant(forms[index], selectedVariant(forms[index], data.variants));
    } else {
      refreshBuyLabel(forms[index]);
    }
  }
})();